import { useCallback, useEffect, useState } from 'react';
import { Role, SnpModule } from '../../../shared/RoleBasedTypes';
import { useAuth } from '../../../hooks/useAuth';
import useUserSession from '../../../hooks/useUserSession';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	addOrUpdateRoleModuleMapping,
	getModules,
	getRoleModulesMapping,
	getRoles,
} from '../../../service/RoleBasedService';
import { displayError } from '../../../shared/Utils';

export const useRolesWithModulesList = () => {
	const [allRoles, setAllRoles] = useState<Array<Role>>([]);
	const [allModules, setAllModules] = useState<Array<SnpModule>>([]);
	const { srcmId } = useAuth();
	const { selectedRole, setAllRolesWithModules } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();

	const fetchAllRoles = useCallback(async () => {
		if (srcmId && selectedRole) {
			setLoading(true);
			try {
				const rolesResponse = await getRoles({
					loggedInUserSrcmId: srcmId,
					selectedRoleCode: selectedRole,
					pageIndex: 0,
					pageSize: 200,
				});

				if (rolesResponse?.roles && Array.isArray(rolesResponse.roles)) {
					setAllRoles(rolesResponse.roles);
				} else {
					setAllRoles([]);
				}
			} catch (e) {
				const message = displayError(e);
				showToastMessage('error', message);
			}
			setLoading(false);
		}
	}, [srcmId, selectedRole, setLoading, showToastMessage]);

	const fetchAllModules = useCallback(async () => {
		if (srcmId && selectedRole) {
			setLoading(true);
			try {
				const modulesResponse = await getModules({
					srcmId,
					selectedRoleCode: selectedRole,
					pageIndex: 0,
					pageSize: 200,
				});

				if (
					modulesResponse?.modules &&
					Array.isArray(modulesResponse.modules)
				) {
					setAllModules(modulesResponse.modules);
				} else {
					setAllModules([]);
				}
			} catch (e) {
				const message = displayError(e);
				showToastMessage('error', message);
			}
			setLoading(false);
		}
	}, [srcmId, selectedRole, setLoading, showToastMessage]);

	const updateModules = useCallback(
		async (roleId: number, moduleIds: number[]) => {
			if (srcmId) {
				setLoading(true);
				try {
					await addOrUpdateRoleModuleMapping({
						srcmId,
						selectedRoleCode: selectedRole || '',
						selectedRoleId: roleId,
						moduleIds,
					});
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				}
				setLoading(false);
			}
		},
		[srcmId, setLoading, selectedRole, showToastMessage]
	);

	const refreshRoleModules = async () => {
		if (!srcmId) return;
		const roleResponse = await getRoleModulesMapping({
			loggedInFunctionarySrcmId: srcmId,
			filters: {},
		});

		setAllRolesWithModules(roleResponse.roleModulesMappings);
		return roleResponse.roleModulesMappings;
	};

	useEffect(() => {
		void fetchAllRoles();
	}, [fetchAllRoles]);

	useEffect(() => {
		void fetchAllModules();
	}, [fetchAllModules]);

	return {
		allRoles,
		allModules,
		updateModules,
		refreshRoleModules,
	};
};
